/**
 * Below are utils primarily focused on Highcharts development
 */
import { AdaptiveContainerAttributesType, CommonChartLabelBottomLeft, CommonHCChartExtended, HtmlString } from "types";
import Highcharts, {
  AlignValue as HCAlignValue,
  OptionsLayoutValue as HCLayoutValue,
  VerticalAlignValue as HCVerticalAlignValue,
} from "highcharts";
import { ThemeFonts, ThemeHexColors, isArray, isNullOrUndefOrEmpty, isString } from "@civicscience/chops";
import { formatDateAndTime, stripHTML } from "utils";

import OfflineExporting from "highcharts/modules/offline-exporting";
import { ResponsiveBreakPoints } from "consts";
import classNames from "classnames";
import highchartsAccessibility from "highcharts/modules/accessibility";
import highchartsExportData from "highcharts/modules/export-data";
import highchartsExporting from "highcharts/modules/exporting";
import highchartsMap from "highcharts/modules/map";

/**
 * Currently only needed in our Highcharts for branding, but this could move to somewhere else if it needs to be re-used somewhere else
 */
export const getChartLogoBase64 = () =>
  `data:image/svg+xml;base64,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`;

//Common defaults
const csLogoSquare = 40;
const hcChartSqMargin = 5;
const hcChartsLegendHeight = 60;
export const hcChartsResponsiveMaxWidth: number = 949;
export const hcChartsResponsiveMediumMaxWidth: number = ResponsiveBreakPoints.Mobile[1];
export const hcChartsResponsiveMobileMaxWidth: number = ResponsiveBreakPoints.MobileSm[1];
export const hcChartsExportWidth: number = hcChartsResponsiveMaxWidth + 1;
export const hcChartsFooterHeight: number = 80;
export const hcChartsExportScale: number = 1.5;
export const hcChartsLegendWidth: number | string = "100%";
export const hcChartsLegendItemWidth: number | undefined = 140;
export const hcChartsLegendSmallWidth: number | string = "100%";
export const hcChartsAnimationDuration: number = 200;
const hcChartsLegendYSpacingHeight = 20;
const hcChartsLegendNoTitleHeight = 15;
const hcChartsLegendLineHeight = 25;
const hcChartsLegendLineItemYMarginSm = 4;
const hcChartsLegendLineItemYMargin = 8;
const hcChartsLegendTitleLineHeight = 20;
const hcChartsLegendTitleLineCount = 130;
const hcChartsLegendTitleLineCountSmallContainer = 90;
const hcChartsTitleLineHeight = 32;
const hcChartsSubTitleLineHeight = 18;
const hcChartsLegendBorderRadius = 3;
export const hcChartsLegendXDefault = 0;
export const hcChartsLegendYDefault = -(hcChartsLegendYSpacingHeight * 2);
export const hcChartsLegendYSmallContainerOffsetDefault = hcChartsLegendLineHeight;
export const hcChartsLegendExportYDefault = -45;
const hcChartsLegendBorderColor = ThemeHexColors.Grayscale300;
const hcChartsLegendBGColor = "rgba(255, 255, 255, .8)"; //White with opacity
const hcChartsLegendSymbolSquare = 16;
export const hcChartsSeriesDataLabelBGColor = "rgba(0, 0, 0, .6)"; //Black with opacity

const hcChartAllowedAttributes = ["datetime"];
const hcChartAllowedTags = ["time"];

export type BasePlotFormatterOptions = {
  point: Highcharts.DataLabelsOptions;
  valueSuffix?: string;
  threshold?: number;
  className?: string;
  addedPointStyles?: string[];
};

export const basePlotFormatter = ({
  point,
  valueSuffix = "%",
  threshold = 4,
  className,
  addedPointStyles = [],
}: BasePlotFormatterOptions): HtmlString => {
  const pointY = point?.y ?? 0;
  const aboveThreshold = pointY > threshold;
  const pointStylesArray: string[] = [
    `display: block;`,
    `text-shadow: ${hcChartsSeriesDataLabelBGColor} 0 0.1em 0.1em;`,
    `background-color: ${hcChartsSeriesDataLabelBGColor};`,
    "padding: .125rem .25rem;",
    "border-radius: .125rem;",
    ...addedPointStyles,
  ];
  const pointStyles = pointStylesArray.join(" ");
  return aboveThreshold
    ? `<span class="${classNames("cs-chart-point-info text-light text-shadow", className)}"
             style="${pointStyles}">
       <span class="cs-chart-point-info-value">${pointY}${valueSuffix}</span>
     </span>`
    : "";
};

export const hasValueInLabelBottomLeftArray = (labelBottomLeft?: CommonChartLabelBottomLeft) => {
  return !isNullOrUndefOrEmpty(labelBottomLeft) && isArray(labelBottomLeft) && labelBottomLeft.length > 0;
};

type CalculateChartLegendTitleHeightProps = {
  titleText?: string;
  legendTitleLineCount?: number;
  legendTitleLineCountSmallContainer?: number;
  legendTitleLineHeight?: number;
  adaptiveContainer?: AdaptiveContainerAttributesType;
};

export const calculateChartLegendTitleHeight = ({
  titleText,
  legendTitleLineCount = hcChartsLegendTitleLineCount,
  legendTitleLineCountSmallContainer = hcChartsLegendTitleLineCountSmallContainer,
  legendTitleLineHeight = hcChartsLegendTitleLineHeight,
  adaptiveContainer,
}: CalculateChartLegendTitleHeightProps): number => {
  const chartLegendTitleLength = titleText?.length ?? 0;
  const lineCount =
    adaptiveContainer?.isContainerMobile || adaptiveContainer?.isContainerTabletSm
      ? legendTitleLineCountSmallContainer
      : legendTitleLineCount;
  const chartLegendLines = Math.ceil(chartLegendTitleLength / lineCount);
  const legendTitleHeight = legendTitleLineHeight * chartLegendLines; // Calculated based on title length for now

  return legendTitleHeight < 1 ? hcChartsLegendNoTitleHeight : legendTitleHeight;
};

type CalculateChartLegendAttributesProps = {
  titleText?: string;
  legendOptionsArray?: string[];
  chartLegendHeightFallback?: number;
  chartLegendYSpacing?: number;
  chartLegendExportY?: number;
  chartLegendLineHeight?: number;
  isLegendAside?: boolean;
  adaptiveContainer: AdaptiveContainerAttributesType;
  showLegend?: boolean;
  showLegendExport?: boolean;
};

type CalculateChartLegendAttributesReturn = {
  legendOptionsArray?: string[];
  chartLegendYSpacing: number;
  chartLegendHeight: number;
  chartLegendExportHeight: number;
  chartLegendNonExportHeight: number;
  chartLegendExportY: number;
};

export const calculateChartLegendAttributes = ({
  titleText,
  isLegendAside = false,
  adaptiveContainer,
  showLegend = false,
  showLegendExport = false,
  legendOptionsArray = [],
  chartLegendExportY = hcChartsLegendExportYDefault,
  chartLegendHeightFallback = hcChartsLegendHeight,
  chartLegendYSpacing = hcChartsLegendYSpacingHeight,
  chartLegendLineHeight = hcChartsLegendLineHeight,
}: CalculateChartLegendAttributesProps): CalculateChartLegendAttributesReturn => {
  const chartLegendTitleHeight = calculateChartLegendTitleHeight({ titleText, adaptiveContainer });

  const {
    isContainerMobile,
    isContainerMobileSm,
    isContainerTablet,
    isContainerTabletSm,
    isContainerNotebook,
    isContainerNotebookLg,
  } = adaptiveContainer;

  const longOptions = legendOptionsArray.filter((element) => element.length > 35);
  const hasLongOptions = longOptions.length > 0;
  const legendLength = legendOptionsArray?.length ?? 0;

  const chartLegendOptionsPerRow = isContainerMobile
    ? isContainerMobileSm
      ? 1
      : 2
    : isContainerTablet
      ? isContainerTabletSm
        ? 4
        : 5
      : isContainerNotebook
        ? isContainerNotebookLg
          ? 9
          : 7.5
        : 11;
  const chartLegendRowLineHeight =
    chartLegendLineHeight + (isContainerMobile ? hcChartsLegendLineItemYMarginSm : hcChartsLegendLineItemYMargin) * 2;
  const chartLegendHeight = isLegendAside
    ? 0
    : legendLength > 0
      ? Math.floor(((legendLength ?? 1) / chartLegendOptionsPerRow) * chartLegendRowLineHeight) +
        chartLegendYSpacing * (hasLongOptions || isContainerMobile ? 2 : 0.5)
      : chartLegendHeightFallback;

  const chartLegendFullHeight = isLegendAside
    ? 0
    : chartLegendYSpacing + chartLegendHeight + chartLegendTitleHeight + chartLegendYSpacing;
  const chartLegendExportHeight = showLegendExport ? chartLegendFullHeight : 0;
  const chartLegendNonExportHeight = showLegend ? chartLegendFullHeight : 0;

  return {
    legendOptionsArray,
    chartLegendYSpacing,
    chartLegendHeight,
    chartLegendExportHeight,
    chartLegendNonExportHeight,
    chartLegendExportY,
  };
};

type CalculateBaseChartDimensionsProps = {
  chartPlotAreaHeight?: number;
  adaptiveContainer: AdaptiveContainerAttributesType;
  showTitle?: boolean;
  showSubTitle?: boolean;
  showLabelBottomLeft?: boolean;
  showLegend?: boolean;
  isLegendAside?: boolean;
  showLegendExport?: boolean;
  chartTitle?: string;
  legendTitle?: string;
  legendOptionsArray?: string[];
  chartSubTitle?: string;
  labelBottomLeft?: CommonChartLabelBottomLeft;
  subTitleLineHeight?: number;
  legendTitleLineHeight?: number;
  titleLineHeight?: number;
  legendTitleLineCount?: number;
};

type CalculateBaseChartDimensionsReturn = {
  showTitles: boolean;
  hasTitle: boolean;
  hasSubTitle: boolean;
  hasBottomLeft: boolean;
  hasBottomLeftVisible: boolean;
  chartXSpacing: number;
  chartYSpacing: number;
  chartYMargin: number;
  chartXMargin: number;
  chartLegendExportY: number;
  chartLegendHeight: number;
  chartHeaderHeight: number;
  chartSubTitleHeight: number;
  chartTitleGap: number;
  chartLegendYSpacing: number;
  chartLegendExportHeight: number;
  chartLegendNonExportHeight: number;
  chartFooterHeight: number;
  chartExportMarginBottom: number;
  chartExportMarginTop: number;
  chartMarginTop: number;
  chartMarginBottom: number;
  chartExportHeight: number;
  chartNonExportHeight: number;
  chartExportWidth: number;
};

export const calculateBaseChartDimensions = ({
  chartPlotAreaHeight = 300,
  adaptiveContainer,
  showTitle = false,
  showSubTitle = false,
  showLabelBottomLeft = false,
  showLegend = false,
  isLegendAside = false,
  showLegendExport = false,
  legendOptionsArray = [],
  chartTitle,
  chartSubTitle,
  labelBottomLeft,
  legendTitle,
  titleLineHeight = hcChartsTitleLineHeight,
  subTitleLineHeight = hcChartsSubTitleLineHeight,
}: CalculateBaseChartDimensionsProps): CalculateBaseChartDimensionsReturn => {
  const showTitles = showTitle || showSubTitle;
  const hasTitle = !isNullOrUndefOrEmpty(chartTitle);
  const hasSubTitle = !isNullOrUndefOrEmpty(chartSubTitle);
  const hasBottomLeft = hasValueInLabelBottomLeftArray(labelBottomLeft);
  const hasBottomLeftVisible = hasBottomLeft && showLabelBottomLeft;

  const { chartHeaderHeight, chartSubTitleHeight, chartTitleGap } = getHCChartTitleHeights({
    chartTitle,
    chartSubTitle,
    subTitleLineHeight,
    titleLineHeight,
  });

  const chartXSpacing = 10;
  const chartYSpacing = 20;
  const chartYMargin = 50; // Spacing enough for the menu icon
  const chartXMargin = 10;

  const {
    chartLegendHeight,
    chartLegendYSpacing,
    chartLegendExportHeight,
    chartLegendNonExportHeight,
    chartLegendExportY,
  } = calculateChartLegendAttributes({
    legendOptionsArray,
    isLegendAside,
    titleText: legendTitle,
    showLegend,
    showLegendExport,
    adaptiveContainer,
  });

  const chartFooterHeight = hcChartsFooterHeight;
  const chartExportMarginTop = chartHeaderHeight ? chartHeaderHeight : chartYMargin;
  const chartMarginTop = showTitles ? chartExportMarginTop : chartYMargin;
  const chartExportMarginBottom = chartLegendExportHeight + chartYSpacing + chartYMargin;
  const chartMarginBottom = chartYMargin + chartLegendNonExportHeight;
  const chartExportHeight = chartHeaderHeight + chartYSpacing * 2 + chartPlotAreaHeight + chartExportMarginBottom;
  const chartNonExportHeight =
    (showTitles ? chartHeaderHeight : 0) + chartYSpacing + chartPlotAreaHeight + chartMarginBottom;
  const chartExportWidth = hcChartsExportWidth;

  return {
    showTitles,
    hasTitle,
    hasSubTitle,
    hasBottomLeft,
    hasBottomLeftVisible,
    chartXSpacing,
    chartYSpacing,
    chartYMargin,
    chartXMargin,
    chartHeaderHeight,
    chartSubTitleHeight,
    chartTitleGap,
    chartLegendExportY,
    chartLegendHeight,
    chartLegendYSpacing,
    chartLegendExportHeight,
    chartLegendNonExportHeight,
    chartFooterHeight,
    chartExportMarginTop,
    chartExportMarginBottom,
    chartMarginTop,
    chartMarginBottom,
    chartNonExportHeight,
    chartExportHeight,
    chartExportWidth,
  };
};

export const getHCMenuItems = () => {
  return [
    "viewFullscreen",
    "printChart",
    "separator",
    "downloadPNG",
    "downloadJPEG",
    "downloadPDF",
    "downloadSVG",
    "separator",
    "downloadCSV",
    "viewData",
  ];
};

type HCTooltipConfigConfig = {
  className?: string;
  valueSuffix?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: number;
  borderWidth?: number;
  padding?: number;
};

export const getHCTooltipConfig = ({
  className,
  valueSuffix,
  backgroundColor = ThemeHexColors.Light,
  borderColor = ThemeHexColors.Secondary,
  borderRadius = 0,
  borderWidth = 2,
  padding = 2,
}: HCTooltipConfigConfig) => {
  return {
    className: classNames("cs-chart-tooltip", className),
    crosshairs: true,
    backgroundColor,
    borderColor,
    borderRadius,
    borderWidth,
    padding,
    shared: true,
    stickOnContact: true,
    shadow: true,
    useHTML: true,
    valueSuffix,
  };
};

type HCBackTrackConfig = {
  trackName?: string;
  backgroundColor?: string;
};

/**
 * Creates a light gray bar underneath the main 'Responses' data series that spans the entire yAxis.
 *
 */
export const getHCBackTrackConfig = ({
  trackName = "",
  backgroundColor = ThemeHexColors.Grayscale100,
}: HCBackTrackConfig) => {
  return {
    showInLegend: false,
    name: trackName,
    color: backgroundColor,
    enableMouseTracking: false,
    dataLabels: {
      showInLegend: false,
      enabled: false,
    },
  };
};

type GetHCChartSharedOptionsProps = {
  showLegend?: boolean;
  legendReversed?: boolean;
  includeLegend?: boolean;
  legendTitle?: string;
  legendX?: number;
  legendY?: number;
  legendWidth?: number | string;
  legendAlign?: "left" | "center" | "right";
  legendVerticalAlign?: "top" | "middle" | "bottom";
  legendLayout?: "vertical" | "horizontal" | "proximate";
  adaptiveContainer?: AdaptiveContainerAttributesType;
};
export const getHCChartSharedOptions = ({
  showLegend = false,
  legendReversed = false,
  includeLegend = true,
  legendTitle,
  legendX = hcChartsLegendXDefault,
  legendY = hcChartsLegendYDefault,
  legendWidth = hcChartsLegendWidth,
  legendAlign = "center",
  legendLayout = "horizontal",
  legendVerticalAlign = "bottom",
  adaptiveContainer,
}: GetHCChartSharedOptionsProps) => {
  const itemWidth = adaptiveContainer?.isContainerMobileSm ? undefined : hcChartsLegendItemWidth; //Force itemWidth above isContainerMobileSm
  const updatedLegendLayout =
    (adaptiveContainer?.containerWidth || 0) > 0 && adaptiveContainer?.isContainerMobileSm ? "vertical" : legendLayout; //Force vertical for isContainerMobileSm

  return {
    accessibility: {
      enabled: true,
    },

    credits: {
      enabled: false,
    },

    legend: includeLegend
      ? {
          align: legendAlign,
          title: {
            text: legendTitle,
            style: {
              color: ThemeHexColors.Grayscale600,
              fontWeight: "bold",
            },
          },
          itemDistance: 10,
          itemMarginTop: hcChartsLegendLineItemYMargin,
          itemMarginBottom: hcChartsLegendLineItemYMargin,
          itemStyle: {
            fontWeight: "normal",
            fontSize: 10,
            textOverflow: false,
          },
          itemWidth,
          padding: 5,
          reversed: legendReversed,
          enabled: showLegend,
          symbolRadius: 0,
          symbolPadding: 5,
          symbolWidth: hcChartsLegendSymbolSquare,
          symbolHeight: hcChartsLegendSymbolSquare,
          shadow: false,
          borderWidth: 0,
          borderColor: hcChartsLegendBorderColor,
          borderRadius: hcChartsLegendBorderRadius,
          backgroundColor: hcChartsLegendBGColor,
          x: legendX,
          y: updatedLegendLayout === "vertical" ? 0 : legendY,
          verticalAlign: legendVerticalAlign,
          width: legendWidth,
          layout: updatedLegendLayout,
          // height: updatedLegendLayout === "vertical" ? 500 : undefined,
        }
      : undefined,

    navigation: getHCNavigationMenuConfig({}),

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: hcChartsResponsiveMaxWidth,
          },
          chartOptions: {
            legend: includeLegend
              ? {
                  width: legendWidth,
                  itemStyle: {
                    fontWeight: "normal",
                    fontSize: 10,
                    textOverflow: null,
                  },
                }
              : undefined,
          },
        },
      ],
    },
  };
};

type HCTitleConfig = {
  text?: string;
  align?: HCAlignValue;
  floating?: boolean;
  fontSize?: number | string;
  fontWeight?: "bold" | "normal";
  fontFamily?: string;
  color?: string;
  useHTML?: boolean;
  enabled?: boolean;
  y?: number;
  widthAdjust?: number;
};

export const getHCTitleConfig = ({
  text,
  align = "left",
  useHTML = true,
  y = 10,
  fontSize = "20px",
  fontWeight = "normal",
  fontFamily = ThemeFonts.default,
  color = ThemeHexColors.Black,
  floating = false,
  widthAdjust = -100,
  enabled = false,
}: HCTitleConfig) => {
  const verticalAlign: HCVerticalAlignValue = "top";

  return {
    enabled,
    text,
    align,
    useHTML,
    floating,
    y,
    widthAdjust,
    verticalAlign,
    style: {
      color,
      fontFamily,
      fontSize,
      fontWeight,
    },
  };
};

export const getHCSubTitleConfig = ({
  text,
  align,
  useHTML,
  y,
  fontSize = "13px",
  fontWeight = "normal",
  color = ThemeHexColors.Grayscale700,
  floating,
  enabled,
}: HCTitleConfig) => {
  return getHCTitleConfig({
    text,
    align,
    useHTML,
    y,
    fontSize,
    fontWeight,
    color,
    floating,
    enabled,
  });
};

export type GetHCChartTitleHeightsProps = {
  chartTitle?: string;
  chartSubTitle?: string;
  subTitleLineCount?: number;
  tabletSubTitleLineCount?: number;
  mobileSubTitleLineCount?: number;
  subTitleLineHeight?: number;
  titleLineCount?: number;
  tabletTitleLineCount?: number;
  mobileTitleLineCount?: number;
  titleLineHeight?: number;
  isViewMD?: boolean;
  isViewSM?: boolean;
};

export type GetHCChartTitleHeightsReturn = {
  chartTitleGap: number;
  chartTitleHeight: number;
  chartSubTitleHeight: number;
  chartHeaderHeight: number;
};

export const getHCChartTitleHeights = ({
  chartTitle = "",
  chartSubTitle = "",
  subTitleLineCount = 146,
  tabletSubTitleLineCount = 100,
  mobileSubTitleLineCount = 80,
  titleLineCount = 90,
  tabletTitleLineCount = 80,
  mobileTitleLineCount = 50,
  titleLineHeight = hcChartsTitleLineHeight,
  subTitleLineHeight = hcChartsSubTitleLineHeight,
  isViewMD = false,
  isViewSM = false,
}: GetHCChartTitleHeightsProps): GetHCChartTitleHeightsReturn => {
  const chartTitleGap = 15;
  const chartTitleLength = chartTitle?.length ?? 0;
  const chartTitleLineLength = isViewSM ? mobileTitleLineCount : isViewMD ? tabletTitleLineCount : titleLineCount; //NOTE: This is hardcoded calculation depending on font size and font.
  const chartTitleLines = Math.ceil(chartTitleLength / chartTitleLineLength);
  const chartTitleHeight = titleLineHeight * chartTitleLines + chartTitleGap; // Calculated based on title length for now

  const chartSubTitleLength = chartSubTitle?.length ?? 0;
  const chartSubTitleLineLength = isViewSM
    ? mobileSubTitleLineCount
    : isViewMD
      ? tabletSubTitleLineCount
      : subTitleLineCount; //NOTE: This is hardcoded calculation depending on font size and font AND SCREEN WIDTH
  const chartSubTitleLines = Math.ceil(chartSubTitleLength / chartSubTitleLineLength);
  const chartSubTitleHeight = subTitleLineHeight * chartSubTitleLines + chartTitleGap; // Calculated based on title length for now

  const chartHeaderHeight = chartTitleHeight + chartTitleGap + chartSubTitleHeight;

  return {
    chartTitleGap,
    chartTitleHeight,
    chartSubTitleHeight,
    chartHeaderHeight,
  };
};

type SharedChartClassNamesProps = {
  showTitle?: boolean;
  showSubTitle?: boolean;
};

export const getHCSharedChartClassNames = ({ showTitle = false, showSubTitle = false }: SharedChartClassNamesProps) => {
  return {
    "cs-show-title": showTitle,
    "cs-hide-title": !showTitle,
    "cs-show-subtitle": showSubTitle,
    "cs-hide-subtitle": !showSubTitle,
  };
};

type GetHCNavigationMenuConfigProps = {
  align?: HCAlignValue;
  verticalAlign?: HCVerticalAlignValue;
  y?: number;
};

export const getHCNavigationMenuConfig = ({
  align = "right",
  verticalAlign = "top",
  y = 0,
}: GetHCNavigationMenuConfigProps) => {
  return {
    buttonOptions: { align, verticalAlign, y },
  };
};

type GetHCMapLegendConfigProps = {
  enabled?: boolean;
  titleText?: string;
  align?: HCAlignValue;
  verticalAlign?: HCVerticalAlignValue;
  layout?: HCLayoutValue;
  symbolRadius?: number;
  borderRadius?: number;
  backgroundColor?: string;
  borderWidth?: number;
  legendX?: number;
  legendY?: number;
};

export const getHCMapLegendConfig = ({
  enabled = true,
  titleText = "Responses",
  align = "right",
  verticalAlign = "bottom",
  layout = "vertical",
  symbolRadius = 0,
  borderRadius = hcChartsLegendBorderRadius,
  backgroundColor = hcChartsLegendBGColor,
  legendX = hcChartsLegendXDefault,
  legendY = hcChartsLegendYDefault,
}: GetHCMapLegendConfigProps) => {
  return {
    enabled,
    title: {
      text: titleText,
    },
    layout,
    borderRadius,
    align,
    verticalAlign,
    backgroundColor,
    borderWidth: 1,
    borderColor: hcChartsLegendBorderColor,
    itemStyle: {
      fontWeight: "normal",
      fontSize: 11,
    },
    itemDistance: 10,
    itemMarginTop: hcChartsLegendLineItemYMargin,
    itemMarginBottom: hcChartsLegendLineItemYMargin,
    padding: 10,
    symbolRadius,
    symbolPadding: 10,
    symbolWidth: hcChartsLegendSymbolSquare,
    symbolHeight: hcChartsLegendSymbolSquare,
    shadow: false,
    x: legendX,
    y: legendY,
  };
};

export const setHCChartOptions = (
  hc: typeof Highcharts,
  showExport = false,
  hcOptionsOverride?: Highcharts.Options,
) => {
  const hcOpts = Object.assign(
    {},
    {
      chart: {
        style: {
          fontFamily: ThemeFonts.default,
        },
      },
      labels: {
        style: {
          fontFamily: ThemeFonts.default,
        },
      },
      lang: {
        thousandsSep: ",",
      },
    },
    hcOptionsOverride,
  );

  hc.setOptions(hcOpts);
  highchartsAccessibility(Highcharts);
  if (showExport) {
    //NOTE: we need to identify certain HTML tags and attributes for Highcharts export.
    // Read more @ https://www.highcharts.com/docs/chart-concepts/security
    hcChartAllowedAttributes.forEach((attr) => hc.AST.allowedAttributes.push(attr));
    hcChartAllowedTags.forEach((tag) => hc.AST.allowedTags.push(tag));
    highchartsExporting(Highcharts);
    OfflineExporting(Highcharts);
    highchartsExportData(Highcharts);
  }
  //NOTE: Originally highchartsMap inclusion was behind an isGeo boolean, but there was a bug when 2 charts were
  // loaded on a page with the second being a map. The menu button in the first chart would cause a type error.
  highchartsMap(Highcharts);
};

export const cleanHCBarChartDataTableFooter = (hc: typeof Highcharts) => {
  //NOTE: Issue in Highcharts github here @ https://github.com/highcharts/highcharts/issues/16536
  //NOTE: Method below explained here @ https://www.highcharts.com/forum/viewtopic.php?f=9&t=46056
  // and also here @ https://jsfiddle.net/BlackLabel/8qz4nmw9/
  //FIXME: Typing to use? https://api.highcharts.com/class-reference/Highcharts.html#.addEvent%3CT%3E or
  // https://api.highcharts.com/class-reference/Highcharts#.EventCallbackFunction%3CT%3E
  hc.addEvent(hc.Chart, "aftergetTableAST", (e) => {
    //eslint-disable-next-line
    //@ts-ignore
    e.tree.children[0].textContent = stripHTML(e.tree.children[0].textContent);
  });
};

export const cleanHCBarChartCSVExportData = (e: Highcharts.ExportDataEventObject, addPercentSign = false) => {
  return e.dataRows.forEach((row) => {
    row.forEach((el: string | number, i) => {
      if (isString(el)) {
        row[i] = stripHTML(el);
      } else if (!isNaN(el)) {
        row[i] = `${el}${addPercentSign ? "%" : ""}`;
      }
    });
  });
};

export const getHCColumnHeaderFormatter = (categoryTitle: string, item: Highcharts.Axis | { name: string }) => {
  if (!item || item instanceof Highcharts.Axis) {
    return categoryTitle;
  } else {
    return item.name;
  }
};

export const getHCCreditText = () => {
  const { date: generationDate, time: generationTime } = formatDateAndTime({ date: new Date() });

  return `Generated by CivicScience &copy; on<br/>${generationDate} at ${generationTime}`;
};

type RenderHCHorizontalLineProps = {
  chart: CommonHCChartExtended;
  lineTopY?: number;
};

export const renderHCHorizontalLine = ({
  chart,
  lineTopY = csLogoSquare + hcChartSqMargin,
}: RenderHCHorizontalLineProps) => {
  /** to avoid issues with resize */
  chart?.horizontalLine?.destroy();

  const renderer = chart.renderer;
  const chartHeight = chart.chartHeight;
  const chartWidth = chart.chartWidth;
  const lineY = chartHeight - lineTopY;
  const pathArray: Highcharts.SVGPathArray = [
    ["M", hcChartSqMargin, lineY - hcChartSqMargin],
    ["H", chartWidth - hcChartSqMargin, hcChartSqMargin],
  ];
  chart.horizontalLine = renderer
    .path(pathArray)
    .attr({
      "stroke-width": 1,
      stroke: ThemeHexColors.Grayscale300,
      id: "chart-hz-line",
    })
    .add();
};

type RenderHCBrandingLogoProps = {
  chart: CommonHCChartExtended;
  verticalAlign?: HCVerticalAlignValue;
};

export const renderHCBrandingLogo = ({ chart, verticalAlign = "bottom" }: RenderHCBrandingLogoProps) => {
  /** to avoid issues with resize */
  chart?.customCreditsImg?.destroy();

  const chartHeight = chart.chartHeight;
  const chartWidth = chart.chartWidth;
  const renderer = chart.renderer;
  const isLogoTop = verticalAlign === "top";

  chart.customCreditsImg = renderer
    .image(
      getChartLogoBase64(),
      chartWidth - csLogoSquare - hcChartSqMargin,
      isLogoTop ? hcChartSqMargin : chartHeight - csLogoSquare - hcChartSqMargin,
      csLogoSquare,
      csLogoSquare,
    )
    .add();
};

type RenderHCBrandingAreasProps = {
  chart: CommonHCChartExtended;
  creditText?: string;
  logoVerticalAlign?: HCVerticalAlignValue;
};

export const renderHCBrandingAreas = ({
  chart,
  creditText = getHCCreditText(),
  logoVerticalAlign = "bottom",
}: RenderHCBrandingAreasProps) => {
  /** to avoid issues with resize */
  chart?.customCredits?.destroy();

  const chartHeight = chart.chartHeight;
  const chartWidth = chart.chartWidth;
  const renderer = chart.renderer;
  const isLogoTop = logoVerticalAlign === "top";
  const creditSqMargin = 3;

  //NOTE: For now the logoVerticalAlign is defaulted as 'bottom', but for 'top'
  // there might be extra work to the width so that it doesn't stack
  const customCreditsLabel = () => {
    const creditLabel = renderer
      .label(creditText, 0, chartHeight)
      .css({
        color: ThemeHexColors.Grayscale600,
        fontSize: "12px",
        fontWeight: "normal",
        fontStyle: "normal",
        textAlign: "right",
      })
      .add();
    const { width: labelWidth, height: labelHeight } = creditLabel.getBBox();

    const creditX = isLogoTop
      ? chartWidth - creditSqMargin - labelWidth
      : chartWidth - creditSqMargin - csLogoSquare - labelWidth;

    creditLabel.attr({
      x: creditX,
      y: chartHeight - hcChartSqMargin - labelHeight - creditSqMargin,
    });

    return creditLabel;
  };

  //TODO: Need to figure out TS issues with the svg renderer before drawing the horizontal line.
  // This may also need to affect calculations
  renderHCHorizontalLine({ chart });
  chart.customCredits = customCreditsLabel();
  renderHCBrandingLogo({ chart, verticalAlign: logoVerticalAlign });
};

type RenderHCLeftBottomAreaProps = {
  chart: CommonHCChartExtended;
  labelBottomLeft?: CommonChartLabelBottomLeft;
  labelBottomLeftLineHeight?: number;
};

export const renderHCLeftBottomArea = ({
  chart,
  labelBottomLeft,
  labelBottomLeftLineHeight = 16,
}: RenderHCLeftBottomAreaProps) => {
  /** to avoid issues with resize */
  chart?.labelBottomLeftElem?.destroy();

  const customBottomLeftLabel = () => {
    if (
      isNullOrUndefOrEmpty(labelBottomLeft) ||
      !isArray(labelBottomLeft) ||
      (isArray(labelBottomLeft) && labelBottomLeft.length < 1)
    ) {
      return undefined;
    }

    const chartHeight = chart.chartHeight;
    const renderer = chart.renderer;

    const emptyLabelBottomLeftLine = "&nbsp;";
    const labelBottomLeftHtml = (labelBottomLeft.length === 1 ? [...labelBottomLeft] : labelBottomLeft)
      .map((label) => {
        return isNullOrUndefOrEmpty(label) ? emptyLabelBottomLeftLine : label;
      })
      .join("<br/>");

    const maxLines = 3;
    const labelHeight = maxLines * labelBottomLeftLineHeight;

    const labelBottomLeftElem = renderer
      .label(`<span>${labelBottomLeftHtml}</span>`, 0, 0, undefined, undefined, undefined, true)
      .css({
        color: ThemeHexColors.Grayscale600,
        fontSize: "11px",
        fontWeight: "normal",
        fontStyle: "normal",
        lineHeight: 1.25,
      })
      .add();

    const labelBottomLeftY = chartHeight - labelHeight + (labelBottomLeft.length < 3 ? hcChartSqMargin : 0);

    labelBottomLeftElem.attr({
      x: hcChartSqMargin,
      y: labelBottomLeftY,
    });

    return labelBottomLeftElem;
  };

  chart.labelBottomLeftElem = customBottomLeftLabel();
};

type RenderHCAreasProps = {
  chart: CommonHCChartExtended;
  labelBottomLeft?: CommonChartLabelBottomLeft;
  creditText?: string;
  showLabelBottomLeft?: boolean;
};

export const renderHCAreas = ({
  chart,
  labelBottomLeft,
  showLabelBottomLeft = false,
  creditText,
}: RenderHCAreasProps) => {
  showLabelBottomLeft && renderHCLeftBottomArea({ chart, labelBottomLeft });
  renderHCBrandingAreas({ chart, creditText });
};

type GetMultiBarChartTooltipFormatterArgs = {
  tooltipHeaderFormat: string;
  tooltipPointFormat: string;
  tooltipFooterFormat: string;
};

type HighchartPoint = Highcharts.TooltipFormatterContextObject;

type HighchartPointWithAccessibility = HighchartPoint & {
  accessibility?: { description?: string };
};

/**
 * Compares 2 points using the series index value.
 * We use the index to indicate the proper ordering of a series.
 * This is especially relevant with multi stacked bar charts that rely on the index for ordering.
 */
const compareIndexedPoint = (
  p1: Highcharts.TooltipFormatterContextObject,
  p2: Highcharts.TooltipFormatterContextObject,
) => p2.series.index - p1.series.index;

/**
 * Builds a string for a specific point to be included in the tooltip.
 *
 * TODO: determine when "accessibility" is actually present and if we need to include it here.
 * The type provided by Highcharts does NOT include it and I didn't see it in the data itself.
 * If we don't need it, we can remove the `HighchartPointWithAccessibility` type.
 */
const getPointTooltipString = (tooltipPointFormat: string, point: HighchartPointWithAccessibility) =>
  tooltipPointFormat
    .replace("{series.name}", point.series.name)
    .replace("{point.y}", String(point.y))
    .replace("{point.accessibility.description}", point.accessibility?.description || "");

/**
 * Returns a tooltip formatter function for CSStackedBarChart with multi enabled.
 * Note that this will sort the data points according to the `index` value of the points.
 * This ensures that the tooltip is ordered correctly even if the data has been reversed.
 * There is no "reversed" property in HighCharts for tooltips:
 * https://github.com/highcharts/highcharts/issues/14013
 */
export const getMultiBarChartTooltipFormatter = ({
  tooltipHeaderFormat,
  tooltipPointFormat,
  tooltipFooterFormat,
}: GetMultiBarChartTooltipFormatterArgs) => {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const orderedPoints = [...(this?.points || [])].sort(compareIndexedPoint);
    const formattedPoints = orderedPoints.map((point) => getPointTooltipString(tooltipPointFormat, point));

    return [tooltipHeaderFormat, ...formattedPoints, tooltipFooterFormat].join("");
  };
};

/**
 * Cleans and escapes a CSV field by ensuring it is properly formatted for CSV.
 * This function handles special characters such as commas, quotes, and newlines.
 *
 * - If the field contains a comma, newline, or double quote, the field will be wrapped
 *   in double quotes.
 * - Any double quotes inside the field will be escaped by doubling them (as per CSV format).
 *
 * See https://www.papaparse.com/docs#parsing for more info.
 *
 * @param {string} field - The field (string) to be cleaned and escaped.
 * @returns {string} - The cleaned and CSV-compliant field.
 */
export const cleanCSVField = (field: string): string => {
  // Check if the field contains characters that need escaping
  const needsEscaping = /[",\n\r]/.test(field);

  if (needsEscaping) {
    // Escape any double quotes by doubling them
    const escaped = field.replace(/"/g, '""');
    // Wrap the field in double quotes
    return `"${escaped}"`;
  }

  return field;
};
